import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Mic } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * BD-18 — the Thought Leadership studio, for the author of a piece: notes in, five sourced drafts out.
 *
 * EVERYTHING IS A DRAFT until the author approves it, and a draft with an unsourced claim cannot be approved: each
 * unsourced claim says why, and is fixed (a real quote from the notes, or a research source with the words it
 * supports) or removed. Who the piece is for is suggested per relationship owner; the author sees only counts. Nothing
 * here sends anything.
 */

interface Claim {
  text: string;
  status: 'sourced' | 'unsourced';
  why?: string;
}
interface Asset {
  id: number;
  kind: string;
  title: string;
  body: string;
  claims: Claim[];
  status: 'draft' | 'needs_sources' | 'ready' | 'approved' | 'failed';
  error: string | null;
  unsourced: number;
}
interface Source { index: number; url: string; title: string }
interface Piece { id: number; title: string; topics: string[]; status: string; sources: Source[]; assets: Asset[] }
interface PieceRow { id: number; title: string; status: string }

const KIND_LABEL: Record<string, string> = {
  brief: 'Point-of-view brief', linkedin: 'LinkedIn post', client_email: 'Client email', roundtable: 'Roundtable topic and invitation', talking_points: 'Two-minute talking points',
};
const STATUS_LABEL: Record<string, string> = { draft: 'Draft', needs_sources: 'Needs sources', ready: 'Ready to approve', approved: 'Approved', failed: 'Not written' };
const STATUS_CLASS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600', needs_sources: 'bg-amber-50 text-amber-700', ready: 'bg-blue-50 text-blue-700', approved: 'bg-green-50 text-green-700', failed: 'bg-red-50 text-red-700',
};

const AssetCard: React.FC<{ asset: Asset; sources: Source[]; busy: boolean; onChanged: () => void; setBusy: (b: boolean) => void }> = ({ asset, sources, busy, onChanged, setBusy }) => {
  const [text, setText] = useState(asset.body);
  const [fixing, setFixing] = useState<number | null>(null);
  const [quote, setQuote] = useState('');
  const [srcIndex, setSrcIndex] = useState(sources[0] ? String(sources[0].index) : '');
  const [support, setSupport] = useState('');
  useEffect(() => setText(asset.body), [asset.body]);

  const run = async (fn: () => Promise<any>, ok?: string) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      setFixing(null);
      setQuote('');
      setSupport('');
      onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-testid={`tl-asset-${asset.kind}`}>
      <div className="flex items-center justify-between">
        <h4 className="font-semibold">{KIND_LABEL[asset.kind] || asset.kind}</h4>
        <span className={`text-xs rounded px-2 py-0.5 ${STATUS_CLASS[asset.status] || ''}`}>{STATUS_LABEL[asset.status] || asset.status}</span>
      </div>
      {asset.status === 'failed' ? (
        <p className="mt-2 text-sm text-red-700">{asset.error || 'This draft could not be written.'}</p>
      ) : null}
      <textarea aria-label={`Text of ${KIND_LABEL[asset.kind] || asset.kind}`} className="mt-2 w-full border rounded px-2 py-1 text-sm" rows={asset.kind === 'brief' ? 10 : 5} value={text} onChange={(e) => setText(e.target.value)} />
      <div className="mt-1 flex gap-2">
        <button type="button" disabled={busy || text === asset.body || !text.trim()} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-50"
          onClick={() => run(() => api.patch(`/api/bd/thought-leadership/assets/${asset.id}`, { body: text }), 'Saved and checked again')}>Save text</button>
        <button type="button" disabled={busy || asset.status === 'approved' || asset.status === 'failed' || asset.unsourced > 0} className="px-3 py-1 text-sm rounded bg-blue-600 text-white disabled:opacity-50"
          onClick={() => run(() => api.post(`/api/bd/thought-leadership/assets/${asset.id}/approve`), 'Approved')}>Approve</button>
      </div>
      {asset.unsourced > 0 && (
        <p className="mt-2 text-sm text-amber-800" data-testid={`tl-blocked-${asset.kind}`}>
          {asset.unsourced} claim{asset.unsourced === 1 ? '' : 's'} with no source. This cannot be approved until each has a source or is removed.
        </p>
      )}
      {asset.claims.length > 0 && (
        <ul className="mt-2 space-y-2">
          {asset.claims.map((c, i) => (
            <li key={i} className="text-sm">
              <span className={c.status === 'sourced' ? 'text-green-700' : 'text-amber-800'}>{c.status === 'sourced' ? 'Sourced' : 'No source'}</span>: {c.text}
              {c.status === 'unsourced' && (
                <>
                  <span className="block text-xs text-gray-500">{c.why}</span>
                  <button type="button" disabled={busy} className="mr-3 text-xs text-blue-600 hover:underline" onClick={() => setFixing(fixing === i ? null : i)}>Add a source</button>
                  <button type="button" disabled={busy} className="text-xs text-blue-600 hover:underline" onClick={() => run(() => api.post(`/api/bd/thought-leadership/assets/${asset.id}/claims/${i}/remove`), 'Claim removed')}>Remove this claim</button>
                  {fixing === i && (
                    <div className="mt-1 space-y-1 border border-gray-200 rounded p-2" data-testid={`tl-fix-${asset.kind}-${i}`}>
                      <input aria-label="Quote from the notes" className="w-full border rounded px-2 py-1 text-xs" placeholder="Words from the notes that back this claim" value={quote} onChange={(e) => setQuote(e.target.value)} />
                      <button type="button" disabled={busy || !quote.trim()} className="text-xs px-2 py-0.5 rounded border border-gray-300"
                        onClick={() => run(() => api.post(`/api/bd/thought-leadership/assets/${asset.id}/claims/${i}/source`, { type: 'expert', quote }), 'Checked')}>Use this quote</button>
                      {sources.length > 0 && (
                        <div className="flex gap-1 items-center">
                          <select aria-label="Source" className="border rounded px-1 py-0.5 text-xs" value={srcIndex} onChange={(e) => setSrcIndex(e.target.value)}>
                            {sources.map((s) => <option key={s.index} value={s.index}>{s.index}. {s.title}</option>)}
                          </select>
                          <input aria-label="Words from the source" className="flex-1 border rounded px-2 py-0.5 text-xs" placeholder="Words the source uses for this" value={support} onChange={(e) => setSupport(e.target.value)} />
                          <button type="button" disabled={busy || !support.trim()} className="text-xs px-2 py-0.5 rounded border border-gray-300"
                            onClick={() => run(() => api.post(`/api/bd/thought-leadership/assets/${asset.id}/claims/${i}/source`, { type: 'web', index: Number(srcIndex), support }), 'Checked')}>Use this source</button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const ThoughtLeadershipTab: React.FC = () => {
  const [pieces, setPieces] = useState<PieceRow[]>([]);
  const [piece, setPiece] = useState<Piece | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: '', topics: '', notes: '', research: false });
  const [summary, setSummary] = useState<{ suggested: number; owners: number } | null>(null);
  const [results, setResults] = useState<{ suggested: number; drafted: number; replies: number; meetings: number } | null>(null);

  const loadList = useCallback(async () => {
    try {
      const res = await api.get('/api/bd/thought-leadership/pieces');
      setPieces(res.data.pieces || []);
    } catch {
      toast.error('Could not load your pieces');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const open = useCallback(async (id: number) => {
    try {
      const res = await api.get(`/api/bd/thought-leadership/pieces/${id}`);
      setPiece(res.data.piece);
      setSummary(null);
      api.get(`/api/bd/thought-leadership/pieces/${id}/results`).then((r) => setResults(r.data.results)).catch(() => setResults(null));
    } catch {
      toast.error('Could not open that piece');
    }
  }, []);

  const create = async () => {
    setBusy(true);
    try {
      const res = await api.post('/api/bd/thought-leadership/pieces', { title: form.title, topics: form.topics, input_text: form.notes, research: form.research });
      const id = res.data.piece.id;
      await api.post(`/api/bd/thought-leadership/pieces/${id}/generate`);
      setForm({ title: '', topics: '', notes: '', research: false });
      await loadList();
      await open(id);
      toast.success('Drafts written. Nothing is approved yet.');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not make the drafts');
    } finally {
      setBusy(false);
    }
  };

  // Rewrites every draft the author has not approved yet; an approved draft stays as it is.
  const regenerate = async (id: number) => {
    setBusy(true);
    try {
      await api.post(`/api/bd/thought-leadership/pieces/${id}/generate`);
      await open(id);
      toast.success('Drafts written again. Approved ones were kept.');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not write the drafts');
    } finally {
      setBusy(false);
    }
  };

  const voice = async (file: File | undefined) => {
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    setBusy(true);
    try {
      const res = await api.post('/api/bd/thought-leadership/voice', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, notes: f.notes ? `${f.notes}\n\n${res.data.text}` : res.data.text }));
      toast.success('Transcribed. Check the text before you continue.');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not transcribe that');
    } finally {
      setBusy(false);
    }
  };

  const suggest = async () => {
    if (!piece) return;
    setBusy(true);
    try {
      const res = await api.post(`/api/bd/thought-leadership/pieces/${piece.id}/targets`);
      setSummary({ suggested: res.data.suggested, owners: res.data.owners });
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not suggest people');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  if (piece) {
    return (
      <div className="space-y-4">
        <button type="button" className="text-sm text-blue-600 hover:underline" onClick={() => { setPiece(null); loadList(); }}>Back to my pieces</button>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{piece.title}</h2>
          <button type="button" disabled={busy} className="px-3 py-1 text-sm rounded border border-gray-300" onClick={() => regenerate(piece.id)}>Write the drafts again</button>
        </div>
        <p className="text-sm text-gray-500">Topics: {piece.topics.join(', ')}. {piece.sources.length ? `${piece.sources.length} outside source${piece.sources.length === 1 ? '' : 's'} found.` : 'No outside sources: every claim must rest on your notes.'}</p>
        <ul className="space-y-4">
          {piece.assets.map((a) => (
            <AssetCard key={a.id} asset={a} sources={piece.sources} busy={busy} setBusy={setBusy} onChanged={() => open(piece.id)} />
          ))}
        </ul>
        <section className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="font-semibold">Who it is for</h3>
          <p className="text-sm text-gray-500">People are suggested to the colleague who owns each relationship, each with a reason. They choose who gets a draft, and every draft waits in Approvals. Nothing is sent.</p>
          <button type="button" disabled={busy} onClick={suggest} className="mt-2 px-3 py-1.5 text-sm rounded border border-gray-300">Suggest who this is for</button>
          {summary && (
            <p className="mt-2 text-sm" data-testid="tl-suggest-summary">
              {summary.suggested === 0 ? 'Nobody fits yet: no one has raised these topics.' : `${summary.suggested} suggestion${summary.suggested === 1 ? '' : 's'} made for ${summary.owners} colleague${summary.owners === 1 ? '' : 's'} to review.`}
            </p>
          )}
          {results && results.drafted > 0 && (
            <p className="mt-2 text-sm text-gray-600" data-testid="tl-results">{results.drafted} draft{results.drafted === 1 ? '' : 's'} chosen so far: {results.replies} repl{results.replies === 1 ? 'y' : 'ies'}, {results.meetings} meeting{results.meetings === 1 ? '' : 's'} booked since.</p>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Thought leadership</h2>
        <p className="text-sm text-gray-500">Turn one partner&apos;s thinking into sourced drafts. Everything is a draft until you approve it, and nothing is sent.</p>
      </div>
      <section className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-2">
        <h3 className="font-semibold">New piece</h3>
        <input aria-label="Title" className="w-full border rounded px-2 py-1 text-sm" placeholder="Title (optional)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <input aria-label="Topics" className="w-full border rounded px-2 py-1 text-sm" placeholder="Topics, comma separated (AI governance, model risk)" value={form.topics} onChange={(e) => setForm({ ...form, topics: e.target.value })} />
        <textarea aria-label="Notes" className="w-full border rounded px-2 py-1 text-sm" rows={8} placeholder="The partner's notes or a short interview, in their own words" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="inline-flex items-center gap-1 cursor-pointer text-blue-600"><Mic className="w-4 h-4" /> Add a voice memo
            <input aria-label="Voice memo" type="file" accept="audio/*,video/mp4" className="hidden" onChange={(e) => voice(e.target.files?.[0])} />
          </label>
          <label className="inline-flex items-center gap-1"><input aria-label="Research with sources" type="checkbox" checked={form.research} onChange={(e) => setForm({ ...form, research: e.target.checked })} /> Look for outside sources</label>
        </div>
        <button type="button" disabled={busy || !form.topics.trim() || form.notes.trim().length < 80} onClick={create} className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white disabled:opacity-50">
          {busy ? 'Working…' : 'Make the drafts'}
        </button>
        <p className="text-xs text-gray-500">At least a few sentences of notes and one topic.</p>
      </section>
      <section>
        <h3 className="font-semibold">My pieces</h3>
        {pieces.length === 0 ? <p className="text-sm text-gray-500 mt-1">No pieces yet.</p> : (
          <ul className="mt-2 divide-y divide-gray-100 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
            {pieces.map((p) => (
              <li key={p.id} className="p-3 flex justify-between">
                <span>{p.title}</span>
                <button type="button" className="text-sm text-blue-600 hover:underline" onClick={() => open(p.id)}>Open</button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default ThoughtLeadershipTab;
